import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useBMP } from '../context/BMPContext';
import QuotaCard from '../cards/QuotaCard';
import { useTranslation } from 'react-i18next';
import './ProducerStoreView.css';

const API = process.env.REACT_APP_API_URL;

export default function ProducerStoreView() {
  const { t } = useTranslation();
  const { viewParams, bmpNavigate } = useBMP();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);

  const username = viewParams?.username;

  useEffect(() => {
    if (!username) return;
    setLoading(true);
    axios.get(`${API}/market/store/${username}/`)
      .then(res => setStore(res.data))
      .catch(() => setStore(null))
      .finally(() => setLoading(false));
  }, [username]);

  if (!username) {
    return (
      <div className="bmp-empty">
        <p>{t('bmp_store_not_found')}</p>
        <button className="bmp-btn bmp-btn-primary" onClick={() => bmpNavigate('catalog')}>{t('bmp_back_catalog')}</button>
      </div>
    );
  }

  if (loading) return <div className="bmp-loading">{t('loading_dots')}</div>;

  if (!store) {
    return (
      <div className="bmp-empty">
        <p>{t('bmp_store_not_found')}</p>
        <button className="bmp-btn bmp-btn-primary" onClick={() => bmpNavigate('catalog')}>{t('bmp_back_catalog')}</button>
      </div>
    );
  }

  const { profile: p, farms, quotas, stats } = store;

  return (
    <div className="pstore">
      <button className="bmp-btn-ghost bmp-back" onClick={() => bmpNavigate('catalog')}>
        &#8592; {t('bmp_back_catalog')}
      </button>

      {/* Store header / banner */}
      <div className="pstore-banner">
        {p.banner_url
          ? <img src={p.banner_url} alt="" className="pstore-banner-real" />
          : <div className="pstore-banner-bg" />
        }
        <div className="pstore-banner-content">
          <div className="pstore-avatar">
            {p.photo_url
              ? <img src={p.photo_url} alt="" />
              : <span>{(p.username || '?')[0].toUpperCase()}</span>
            }
          </div>
          <div className="pstore-info">
            <h1 className="pstore-name">
              {p.company_name || p.full_name || `@${p.username}`}
              {p.is_verified && <span className="pstore-verified" title={t('bmp_verified')}>&#10003;</span>}
            </h1>
            <span className="pstore-username">@{p.username}</span>
            <div className="pstore-meta">
              {p.zone_display && <span className="pstore-meta-item">{p.zone_display}</span>}
              {p.city && <span className="pstore-meta-item">{p.city}</span>}
              <span className={`pstore-role-badge bmp-role--${p.role}`}>{p.role_display}</span>
            </div>
          </div>
          <div className="pstore-stats">
            <div className="pstore-stat">
              <span className="pstore-stat-val">{stats.total_farms}</span>
              <span className="pstore-stat-label">{t('bmp_store_farms')}</span>
            </div>
            <div className="pstore-stat">
              <span className="pstore-stat-val">{stats.total_active_quotas}</span>
              <span className="pstore-stat-label">{t('bmp_store_active_quotas')}</span>
            </div>
            <div className="pstore-stat">
              <span className="pstore-stat-val">{stats.total_available_boxes.toLocaleString('es-EC')}</span>
              <span className="pstore-stat-label">{t('bmp_store_available_boxes')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      {p.description && (
        <div className="pstore-desc bmp-card">
          <p>{p.description}</p>
        </div>
      )}

      {/* Farms */}
      {farms.length > 0 && (
        <div className="pstore-section">
          <h2>{t('bmp_store_our_farms')}</h2>
          <div className="pstore-farms-grid">
            {farms.map(f => (
              <div key={f.id} className="pstore-farm bmp-card">
                {f.cover_url && (
                  <img src={f.cover_url} alt={f.name} className="pstore-farm-cover" />
                )}
                <div className="pstore-farm-body">
                  <div className="pstore-farm-header">
                    <strong>{f.name}</strong>
                    <span className={`bmp-zone--${f.zone}`}>{f.zone_display}</span>
                  </div>
                  <div className="pstore-farm-meta">
                    {f.city && <span>{f.city}</span>}
                    {f.hectares && <span>{f.hectares} ha</span>}
                    <span className="bmp-tag">{f.fruit_type_display}</span>
                  </div>
                  {f.certifications && (
                    <div className="pstore-farm-certs">
                      {f.certifications.split(',').map((c, i) => (
                        <span key={i} className="pstore-cert-badge">{c.trim()}</span>
                      ))}
                    </div>
                  )}
                  {f.photos && f.photos.length > 0 && (
                    <div className="pstore-farm-gallery">
                      {f.photos.slice(0, 4).map(p => (
                        <img key={p.id} src={p.image_url} alt={p.caption || p.category_display} className="pstore-farm-thumb" />
                      ))}
                      {f.photos.length > 4 && (
                        <span className="pstore-farm-more">+{f.photos.length - 4}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Available quotas */}
      <div className="pstore-section">
        <h2>{t('bmp_store_available_now')} ({quotas.length})</h2>
        {quotas.length === 0 ? (
          <p className="bmp-empty">{t('bmp_store_no_quotas')}</p>
        ) : (
          <div className="pstore-quotas-grid">
            {quotas.map(q => <QuotaCard key={q.id} quota={q} />)}
          </div>
        )}
      </div>
    </div>
  );
}
